import { format as fm, addMinutes } from 'date-fns';

import { formatToTimezone } from 'Components/date/date-fm';
import { downloadAsFile } from 'Utils/download';

/**
 * Convert a html table to CSV data string
 * @param {ReactRef} ref Reference to the table DOM node
 * @param {*} separator Separator to use to generate the CSV data string
 */
export const tableToCsv = ({ current: rootEl }, separator = ',') => {
  const table = rootEl.querySelector('table');

  const columns = [].map.call(table.querySelectorAll('table > thead > tr:first-child th'), (d) => d.innerText).join(separator);
  const dataRows = [].map.call(
    table.querySelectorAll('table tbody > tr:first-child > td:first-child tr'),
    (row) => [].map.call(row.children, (cell) => cell.innerText).join(separator)
  ).join('\n');

  return [columns, dataRows].join('\n');
};

/**
 * Copy passed text to user's clipboard
 * @param {string} text Text to copy
 */
export const copyToClipboard = async (text) => {
  // 1. Try the modern Clipboard API first
  if (navigator?.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return; // Returns undefined
    } catch (err) { /* empty */ }
  }

  // 2. Fallback to the textarea hack for older browsers
  // We await the Promise instead of returning it so the function consistently returns undefined
  await new Promise((resolve, reject) => {
    const textArea = document.createElement('textarea');
    textArea.value = text;

    Object.assign(textArea.style, {
      top: '0',
      left: '-9999px',
      position: 'fixed',
      opacity: '0', // Replaced number with string for strict React/ESLint environments
    });

    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    if (typeof textArea.setSelectionRange === 'function') {
      textArea.setSelectionRange(0, 999999);
    }

    try {
      const copiedSuccessfully = document.execCommand('copy');
      if (copiedSuccessfully) {
        resolve();
      } else {
        reject(new Error('execCommand returned false'));
      }
    } catch (err) {
      reject(err);
    } finally {
      document.body.removeChild(textArea);
    }
  });
};

/**
 * Get table cell value as a string
 * @param {string} prop The property to get from a data row
 * @param {any} data Data row
 * @param {null|function} value The custom function to retrieve a cell's value
 */
// const stringValue = ({ prop, value }, data) => {
//   if (!value) {
//     return data[prop];
//   }

//   if (typeof value === 'function') {
//     const intermValue = value(data, prop);

//     if (typeof intermValue === 'string') {
//       return intermValue;
//     }

//     // Hacky way to compile a ReactNode object and get it's value
//     return intermValue.type(intermValue.props).props.children;
//   }

//   return value;
// };

const getStringCell = (value, separator) => {
  if (value) {
    return String(value).trim().replaceAll('\n', ' ').replaceAll(separator, ' ');
  }
  return '';
};

const getDateTimeCell = (item, field, dateFormat, separator) => {
  let value = item[field];

  try {
    if (field === 'stop_time' && item.approx) {
      value = addMinutes(new Date(value), item.approx).toString();
    }
    const gmt = fm(formatToTimezone(value, 'GMT'), dateFormat);
    const et = fm(formatToTimezone(value, 'ET'), dateFormat);
    return `${gmt} GMT / ${et} ET`;
  } catch {
    return getStringCell(value, separator);
  }
};

const dataToCsv = (fields, columns, data, dateFormat, separator = ',') => {
  fields = fields.filter((f) => typeof (f) === 'string');
  const header = fields.map((f) => columns.find((c) => c.prop === f)?.label || f).join(separator);

  const rows = data.map((item) => fields.map((field) => {
    if (field.indexOf('time') > -1 || field.indexOf('date') > -1) {
      return getDateTimeCell(item, field, dateFormat, separator);
    }
    return getStringCell(item[field], separator);
  }).join(separator)).join('\n');

  return [header, rows].join('\n');
};

/**
 * Copy a table's data to user's clipboard
 * @param {any} columns Metadata describing a table's columns
 * @param {any} data Data to render table rows
 */
export const copyDataTable = (fields, columns, data, dateFormat) => {
  const csv = dataToCsv(fields, columns, data, dateFormat, '\t');
  return copyToClipboard(csv);
};

/**
 * Export a table's data as downloadable CSV file
 * @param {any} columns Metadata describing a table's columns
 * @param {any} data Data to render table rows
 */
export const downloadDataTable = (fields, columns, data, fileName, dateFormat) => {
  const csv = `${dataToCsv(fields, columns, data, dateFormat, ',')}`;
  downloadAsFile(csv, fileName, false);
};
